import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faBars,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faFileInvoice,
  faSearch,
  faFilter,
  faEdit,
  faTrashAlt,
  faPen,
  faUser,
  faTruck
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase, supabaseAdmin } from '../lib/supabaseClient';
import ExpenseTypesModal from '../components/ExpenseTypeModal';
import ExpenseModal from '../components/ExpenseModal';
import PDFViewer from '../components/PDFViewer';

function Expenses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [expenses, setExpenses] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  
  // Modal State'leri
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);

  // İlişkisel Veriler
  const [drivers, setDrivers] = useState([]);
  const [loads, setLoads] = useState([]);
  
  const [filters, setFilters] = useState({
    search: '',
    driverSearch: '',
    loadSearch: '',
    loadReferenceSearch: '',
    company: '',
    expenseType: '',
    startDate: '',
    endDate: '',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [expensesPerPage, setExpensesPerPage] = useState(25);
  const [totalFilteredExpenses, setTotalFilteredExpenses] = useState(0);

  const [isPDFOpen, setIsPDFOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    const initData = async () => {
      setLoading(true);
      await Promise.all([
        fetchExpsenseTypes(),
        fetchDrivers(),
        fetchLoads(),
        fetchCountries()
      ]);
      setLoading(false);
    };
    initData();
  }, []);

  useEffect(() => {
    if (expenseTypes.length > 0) {
      fetchExpenses(1, true);
    }
  }, [expenseTypes]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchExpenses(1, true);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters, expensesPerPage]);

  const driversMap = useMemo(() => {
    return drivers.reduce((acc, driver) => {
      acc[driver.id] = {
        fullName: `${driver.first_name} ${driver.last_name}`,
        towPlate: driver.tow_plate,
        trailerPlate: driver.trailer_plate
      };
      return acc;
    }, {});
  }, [drivers]);

  const loadsMap = useMemo(() => {
    return loads.reduce((acc, load) => {
      acc[load.id] = {
        formatted_id: load.formatted_id,
        reference_number: load.reference_number
      };
      return acc;
    }, {});
  }, [loads]);

  const countriesMap = useMemo(() => {
    return countries.reduce((acc, country) => {
      acc[country.id] = country.name_tr;
      return acc;
    }, {});
  }, [countries]);

  const fetchExpsenseTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('load_expenses_types')
        .select('*')
        .order('id', { ascending: true });
      if (error) throw error;
      setExpenseTypes(data || []);
    } catch (error) {
      console.error('Hata:', error);
    }
  };

  const fetchDrivers = async () => {
    try {
      let query = supabaseAdmin
        .from('profiles')
        .select('id, first_name, last_name, tow_plate, trailer_plate') 
        .in('user_type', ['driver', 'company_driver'])
        .order('first_name', { ascending: true });
      const { data, error } = await query;
      if (error) throw error;
      setDrivers(data || []);
    } catch (error) {
      console.error('Drivers fetch error:', error);
    }
  };

  const fetchLoads = async () => {
    try {
      let query = supabaseAdmin
        .from('loads')
        .select('id, formatted_id, reference_number');
      const { data, error } = await query;
      if (error) throw error;
      setLoads(data || []);
    } catch (error) {
      console.error('Loads fetch error:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase.from('countries').select('*');
      if (error) throw error;
      setCountries(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const buildQuery = () => {
    let query = supabaseAdmin
      .from('load_expenses')
      .select('*', { count: 'exact' });

    if (filters.expenseType) query = query.eq('expense_type', filters.expenseType);
    if (filters.company) query = query.eq('company_id', filters.company);
    if (filters.startDate) query = query.gte('expense_time', filters.startDate);
    if (filters.endDate) query = query.lte('expense_time', `${filters.endDate}T23:59:59`);
    if (filters.search) query = query.ilike('description', `%${filters.search}%`);

    if (filters.driverSearch) {
      const searchLower = filters.driverSearch.toLowerCase();
      const matchingDriverIds = drivers
        .filter(d => 
          `${d.first_name} ${d.last_name}`.toLowerCase().includes(searchLower)
        )
        .map(d => d.id);
      
      if (matchingDriverIds.length > 0) {
        query = query.in('driver_id', matchingDriverIds);
      } else {
        query = query.eq('id', '00000000-0000-0000-0000-000000000000'); // İmkansız ID
      }
    }

    if (filters.loadSearch) {
      const searchLower = filters.loadSearch.toLowerCase();
      const matchingLoadIds = loads
        .filter(l => 
          l.formatted_id && l.formatted_id.toLowerCase().includes(searchLower)
        )
        .map(l => l.id);

      if (matchingLoadIds.length > 0) {
        query = query.in('load_id', matchingLoadIds);
      } else {
        query = query.eq('id', '00000000-0000-0000-0000-000000000000');// İmkansız ID
      }
    }

    if (filters.loadReferenceSearch) {
      const searchLower = filters.loadReferenceSearch.toLowerCase();
      const matchingLoadIds = loads
        .filter(l => 
          l.reference_number && l.reference_number.toLowerCase().includes(searchLower)
        )
        .map(l => l.id);

      if (matchingLoadIds.length > 0) {
        query = query.in('load_id', matchingLoadIds);
      } else {
        query = query.eq('id', '00000000-0000-0000-0000-000000000000');// İmkansız ID
      }
    }

    return query;
  };

  const fetchExpenses = async (page = currentPage, resetPage = false) => {
    try {
      const loadingState = page === 1 || resetPage ? setLoading : setPageLoading;
      loadingState(true);
      setError(null);

      const offset = (page - 1) * expensesPerPage;
      let query = buildQuery();
      
      query = query
        .order('expense_time', { ascending: false })
        .range(offset, offset + expensesPerPage - 1);

      const { data, error, count } = await query;
      if (error) throw error;

      setTotalFilteredExpenses(count || 0);
      setExpenses(data || []);
      
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setError('Masraflar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Bu masraf kaydını silmek istediğinize emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('load_expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchExpenses(currentPage);
    } catch (err) {
      console.error(err);
      alert('Silme işlemi başarısız.');
    }
  };

  const handleEditClick = (expense) => {
    setSelectedExpense(expense);
    setIsExpenseModalOpen(true);
  };

  const handleAddNewClick = () => {
    setSelectedExpense(null);
    setIsExpenseModalOpen(true);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const getExpenseTypeName = (typeId) => {
    if (!expenseTypes) return typeId;
    const type = expenseTypes.find(t => t.id === typeId);
    return type ? type.name_tr : `Tip ${typeId}`;
  };

  const openReceipt = (filepath) => {
    if (!filepath) return;
    const cleanUrl = filepath.replace(/^__/, '').replace(/__$/, '');
    window.open(cleanUrl, '_blank');
  };

  const totalPages = Math.ceil(totalFilteredExpenses / expensesPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages && pageNumber !== currentPage) {
      setCurrentPage(pageNumber);
      fetchExpenses(pageNumber);
    }
  };

  const handleExpensesPerPageChange = (e) => {
    setExpensesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">

      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>
            <div className="relative">
              <button onClick={handleLogout} className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none">
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-x-hidden overflow-y-auto">
        <div className="container mx-auto px-6 py-8">
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Masraflar</h2>
              <p className="text-sm text-gray-500 mt-1">
                Toplam <span className="font-medium text-gray-900">{totalFilteredExpenses}</span> masraf kaydı bulundu
              </p>
            </div>
            <div className="flex space-x-3">
               <button 
                onClick={() => setIsTypeModalOpen(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center shadow-sm"
              >
                <FontAwesomeIcon icon={faEdit} className="mr-2" />
                Masraf Tipleri
              </button>
              
              <button 
                onClick={handleAddNewClick}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center shadow-sm"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Yeni Masraf
              </button>
            </div>
          </div>




          {/* filtreler */}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
            <div className="flex items-center mb-4 text-gray-800">
              <FontAwesomeIcon icon={faFilter} className="mr-2 text-blue-500" />
              <h3 className="font-semibold">Filtrele</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
              <div className="xl:col-span-1">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Yük No</label>
                <div className="relative">
                  <input 
                    type="text" 
                    name="loadSearch" 
                    placeholder="Yük No Ara..." 
                    value={filters.loadSearch} 
                    onChange={handleFilterChange} 
                    className="w-full pl-8 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" 
                  />
                  <FontAwesomeIcon icon={faTruck} className="absolute left-3 top-3 text-gray-400 text-xs" />
                </div>
              </div>

              <div className="xl:col-span-1">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Yük Reference No</label>
                <div className="relative">
                  <input 
                    type="text" 
                    name="loadReferenceSearch" 
                    placeholder="Ref No Ara..." 
                    value={filters.loadReferenceSearch} 
                    onChange={handleFilterChange} 
                    className="w-full pl-8 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" 
                  />
                  <FontAwesomeIcon icon={faTruck} className="absolute left-3 top-3 text-gray-400 text-xs" />
                </div>
              </div>

              <div className="xl:col-span-1">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Sürücü</label>
                <div className="relative">
                  <input 
                    type="text" 
                    name="driverSearch" 
                    placeholder="Sürücü Ara..." 
                    value={filters.driverSearch} 
                    onChange={handleFilterChange} 
                    className="w-full pl-8 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" 
                  />
                  <FontAwesomeIcon icon={faUser} className="absolute left-3 top-3 text-gray-400 text-xs" />
                </div>
              </div>

              <div className="xl:col-span-1">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Açıklama</label>
                <div className="relative">
                  <input 
                    type="text" 
                    name="search" 
                    placeholder="Detay..." 
                    value={filters.search} 
                    onChange={handleFilterChange} 
                    className="w-full pl-8 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" 
                  />
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-3 text-gray-400 text-xs" />
                </div>
              </div>

              <div className="xl:col-span-1">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Masraf Tipi</label>
                <select name="expenseType" value={filters.expenseType} onChange={handleFilterChange} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                  <option value="">Tümü</option>
                  {expenseTypes?.map((t) => <option key={t.id} value={t.id}>{t.name_tr}</option>)}
                </select>
              </div>

              <div className="xl:col-span-1">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Başlangıç</label>
                <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>

              <div className="xl:col-span-1">
                <label className="block text-xs font-medium text-gray-500 mb-1 uppercase">Bitiş</label>
                <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="w-full px-2 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
            {error && <div className="p-4 bg-red-50 border-b border-red-100 text-red-700">{error}</div>}

            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Veriler yükleniyor...</p>
              </div>
            ) : (
              <>
                <div className={`overflow-x-auto ${pageLoading ? 'opacity-50' : ''}`}>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Yük</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sürücü</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Masraf Tipi</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ülke</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Miktar</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kur</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Toplam (TRY)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Açıklama</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Belge</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {expenses.length === 0 ? (
                        <tr><td colSpan="11" className="px-6 py-10 text-center text-gray-500">Kayıt bulunamadı.</td></tr>
                      ) : (
                        expenses.map((expense) => {
                          const driverInfo = driversMap[expense.driver_id];
                          const loadInfo = loadsMap[expense.load_id];
                          
                          return (
                            <tr key={expense.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 flex items-center">
                                  {loadInfo.formatted_id ? (
                                    <span className="px-2 py-0.5 rounded text-gray-700 bg-gray-100 font-mono text-xs">
                                      {loadInfo.formatted_id}
                                    </span>
                                  ) : <span className="text-gray-400 text-xs">-</span>}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {loadInfo.reference_number || '-'}
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">
                                      {driverInfo ? driverInfo.fullName : 'Bilinmeyen'}
                                    </div>
                                    <div className="flex flex-col">
                                      <div className="text-xs text-gray-500">
                                        {driverInfo ? driverInfo.towPlate : '-'}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {driverInfo ? driverInfo.trailerPlate : '-'}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
                                  ${expense.expense_type === 1 ? 'bg-orange-100 text-orange-800' : 
                                    expense.expense_type === 2 ? 'bg-blue-100 text-blue-800' : 
                                    'bg-gray-100 text-gray-800'}`}>
                                  {getExpenseTypeName(expense.expense_type)}
                                </span>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {expense.country ? countriesMap[expense.country] : '-'}
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-bold text-gray-900">
                                  {expense.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} 
                                  <span className="ml-1 text-xs text-gray-500">{expense.currency_type}</span>
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {expense.exchange_rate ? (
                                    <span className="font-medium text-blue-600">
                                      {expense.exchange_rate.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-green-700">
                                  {expense.currency_type !== 'TRY' && expense.exchange_rate ? (
                                    <>
                                      {(expense.amount * expense.exchange_rate).toLocaleString('tr-TR', { 
                                        minimumFractionDigits: 2 
                                      })} 
                                      <span className="ml-1 text-xs text-gray-500">TRY</span>
                                    </>
                                  ) : expense.currency_type === 'TRY' ? (
                                    <>
                                      {expense.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} 
                                      <span className="ml-1 text-xs text-gray-500">TRY</span>
                                    </>
                                  ) : (
                                    <span className="text-gray-400">-</span>
                                  )}
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{new Date(expense.expense_time).toLocaleDateString('tr-TR')}</div>
                                <div className="text-xs text-gray-500">{new Date(expense.expense_time).toLocaleTimeString('tr-TR', {hour: '2-digit', minute:'2-digit'})}</div>
                              </td>

                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-700 max-w-xs truncate" title={expense.description}>
                                  {expense.description || '-'}
                                </div>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                {expense.filepath ? (
                                  <button onClick={() => {
                                      setPdfUrl(expense.filepath);
                                      setIsPDFOpen(true);
                                    }} 
                                    className="text-blue-600 hover:text-blue-900 flex items-center justify-end w-full"
                                  >
                                    <span className="mr-2">Görüntüle</span>
                                    <FontAwesomeIcon icon={faFileInvoice} />
                                  </button>
                                ) : <span className="text-gray-400 text-xs italic">Belge Yok</span>}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <div className="flex items-center justify-end space-x-5">
                                  <button 
                                    onClick={() => handleEditClick(expense)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                    title="Düzenle"
                                  >
                                    <FontAwesomeIcon icon={faPen} />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteExpense(expense.id)}
                                    className="text-red-600 hover:text-red-900"
                                    title="Sil"
                                  >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Toplam <span className="font-medium">{totalFilteredExpenses}</span> kayıttan{' '}
                        <span className="font-medium">{(currentPage - 1) * expensesPerPage + 1}</span> -{' '}
                        <span className="font-medium">{Math.min(currentPage * expensesPerPage, totalFilteredExpenses)}</span> arası gösteriliyor
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <select value={expensesPerPage} onChange={handleExpensesPerPageChange} className="border border-gray-300 rounded-md text-sm py-1 pl-2 px-1 focus:outline-none focus:ring-1 focus:ring-blue-500">
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1 || pageLoading} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}>
                          <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4" />
                        </button>
                        <div className="flex items-center px-4 border-t border-b border-gray-300 bg-white text-sm text-gray-700">{currentPage} / {totalPages}</div>
                        <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages || pageLoading} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`}>
                          <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <ExpenseTypesModal 
        isOpen={isTypeModalOpen} 
        onClose={() => setIsTypeModalOpen(false)}
        onUpdate={() => {
          fetchExpsenseTypes(); 
        }}
      />

      <ExpenseModal 
        isOpen={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
        onSave={() => fetchExpenses(currentPage)}
        expenseToEdit={selectedExpense}
        drivers={drivers}
        loads={loads}
        expenseTypes={expenseTypes}
      />

      <PDFViewer
        isOpen={isPDFOpen}
        onClose={() => setIsPDFOpen(false)}
        documentUrl={pdfUrl}
      />

    </div>
  );
}

export default Expenses;